import type { AgentId, AgentConfig } from './agents.types';

export const AGENTS: AgentConfig[] = [
	{
		id: 'scene-ai',
		name: 'MitRa',
		version: '1.3',
		description: 'MitRa — creates and manages Shelly Cloud scenes',
		icon: 'mdi-robot-outline',
		avatar: '/assets/agents/bates.png',
		category: 'scene',
		capabilities: ['scene-creation', 'device-discovery', 'scripting', 'knowledge'],
		endpoint: {
			dev: 'http://localhost:3100/api/v2/generate',
			prod: 'https://ai-agent.shelly.cloud/api/v2/generate',
		},
		authMethod: 'bearer',
		maxHistoryMessages: null,
		sessionIdRequired: false,
		supportsSceneCreation: true,
		supportsPromptCaching: true,
		hasDevEndpoint: true,
		responseMetaFields: ['intent', 'toolCallsCount', 'options', 'devicesUsed'],
		healthEndpoint: {
			dev: 'http://localhost:3100/health',
			prod: 'https://ai-agent.shelly.cloud/health',
		},
		supportsHealthCheck: true,
		healthResponseType: 'mitra',
	},
	{
		id: 'brother-eye',
		name: 'SoFi',
		version: '1.5',
		description: 'SoFi — natural-language smart-home control',
		icon: 'mdi-home-assistant',
		avatar: '/assets/agents/alfred.png',
		category: 'control',
		capabilities: ['device-control', 'device-status', 'home-status', 'clarification'],
		endpoint: {
			dev: 'http://localhost:3100/api/v2/generate',
			prod: 'https://ai-agent.shelly.cloud/api/v2/generate',
		},
		authMethod: 'bearer',
		maxHistoryMessages: 6,
		sessionIdRequired: true,
		supportsSceneCreation: false,
		supportsPromptCaching: false,
		hasDevEndpoint: false,
		responseMetaFields: [
			'chosenSkill',
			'classification',
			'tier',
			'traceId',
			'processingTime',
			'needsClarification',
		],
		healthEndpoint: {
			dev: 'http://localhost:3100/health',
			prod: 'https://ai-agent.shelly.cloud/health',
		},
		supportsHealthCheck: false, // Flip to true when ready to enforce
		healthResponseType: 'mitra',
	},
];

export const DEFAULT_AGENT_ID: AgentId = 'scene-ai';

export function getAgent(id: AgentId): AgentConfig {
	const agent = AGENTS.find((a) => a.id === id);
	if (!agent) throw new Error(`Unknown agent: ${id}`);
	return agent;
}

export function getAgentName(id: AgentId): string {
	return getAgent(id).name;
}

export function getAgentSelectOptions(): Array<{
	title: string;
	value: AgentId;
}> {
	return AGENTS.map((a) => ({ title: `${a.name} ${a.version}`, value: a.id }));
}

export function getAgentEndpoint(id: AgentId): string {
	const agent = getAgent(id);
	return import.meta.env.DEV && agent.hasDevEndpoint ? agent.endpoint.dev : agent.endpoint.prod;
}

export function getAgentHealthEndpoint(id: AgentId): string | null {
	const agent = getAgent(id);
	if (!agent.supportsHealthCheck || !agent.healthEndpoint) return null;
	return import.meta.env.DEV && agent.hasDevEndpoint
		? agent.healthEndpoint.dev
		: agent.healthEndpoint.prod;
}

// Extract version based on agent's health response type
// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export function extractVersionFromHealth(id: AgentId, response: any): string | null {
	const agent = getAgent(id);

	switch (agent.healthResponseType) {
		case 'mitra':
			// MitRa: version is at top level
			return response.version || null;

		case 'brother-eye':
			// SoFi: version at top level, or services.llm.version for LLM-specific
			return response.version || response.services?.llm?.version || null;

		default:
			return response.version || null;
	}
}

// Check if agent is healthy based on response type
// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export function isHealthResponseOk(id: AgentId, response: any): boolean {
	const agent = getAgent(id);

	switch (agent.healthResponseType) {
		case 'mitra':
			return response.status === 'ok';

		case 'brother-eye':
			// SoFi: check top-level status AND llm.healthy
			return response.status === 'ok' && response.services?.llm?.healthy !== false;

		default:
			return response.status === 'ok';
	}
}
